import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import {
  CustomMultiSelectFieldConfig,
  CustomSelect,
  FrontEndFieldName,
  Microsoft365License,
  Microsoft365LicenseCount,
  Microsoft365LicenseSettings,
  SelectFieldConfig,
} from '../../../shared/models/generated/ziroModels';
import { ZiroFormConfig } from '../../../forms/ziro-forms-models';
import { ToastService } from '../../../shared/services/toast.service';
import { combineLatest, Observable, of, Subscription, throwError } from 'rxjs';
import { SelectFieldConfigUi } from '../../cisco/dial-plan-mangement/dial-plan-mangement-edit/dial-plan-management-edit.component';
import { difference, sortBy } from 'lodash';
import { Microsoft365LicenseSettingsResource } from '../../../shared/resources/microsoft-365-license-settings.resource';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { catchError, finalize, tap } from 'rxjs/operators';
import {
  ReadWriteConfigType,
  ZiroReadWriteSelectFieldConfig,
} from '../../../shared/custom-configs/ziro-read-write-select-config/ziro-read-write-select-config.component';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { ZiroCustomSelectConfig } from '../../../shared/custom-configs/custom-select/ziro-custom-select.component';

// Custom entity to map Microsoft365LicenseCount to something the UI components can use
export interface MsLicenseManagementEntity {
  usageLocation: SelectFieldConfig<string>;
  licenses: CustomMultiSelectFieldConfig;
}

export interface MsLicenseManagementFormData {
  usage_location: CustomSelect;
  licenses: SelectFieldConfigUi;
}

@Component({
  selector: 'ziro-microsoft-license-management-form',
  templateUrl: './license-management-form.component.html',
  standalone: false,
})
export class MicrosoftLicenseManagementFormComponent
  extends ZiroFormAbstractDirective<MsLicenseManagementEntity, MsLicenseManagementFormData>
  implements OnInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private _bottomNavService = inject(BottomNavService);
  private _toastService = inject(ToastService);
  private _microsoft365LicenseSettingsResource = inject(Microsoft365LicenseSettingsResource);
  private _ziroModalService = inject(ZiroModalService);

  readonly usageLocationOptions = input<string[]>();
  readonly licensesOptions = input<string[]>();
  readonly ms365Licenses = input<Microsoft365LicenseCount[]>();
  readonly tenantId = input<number>();

  type = ReadWriteConfigType.LICENSE;
  formConfig: ZiroFormConfig;

  private _subs = new Subscription();

  ngOnInit(): void {
    this._initBottomNav();
    this._initFormConfig();

    const bottomNavErrorStateSub = combineLatest([
      this._validateAndSubmitSource,
      this.ziroFormsUpdateSource.asObservable(),
    ]).subscribe(() => {
      this._updateBottomNavErrorState();
    });
    this._subs.add(bottomNavErrorStateSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
      }),
    );
    this._subs.unsubscribe();
  }

  private _updateBottomNavErrorState(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: !this.isFormValid(),
      }),
    );
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        usage_location: {
          label: 'tkey;admin.microsoft.license_management.usage_location.label',
          dataAutomation: 'ms-license-management-usage-location',
          componentConfig: new ZiroCustomSelectConfig({
            modalHeader: 'tkey;admin.microsoft.license_management.usage_location.label',
            modalDescription: 'tkey;admin.microsoft.license_management.usage_location.modal.about',
            displayValues: sortBy(this.usageLocationOptions()),
            availableOptions: sortBy(this.usageLocationOptions()),
            isAlwaysRequired: true,
          }),
          helpText: 'tkey;admin.microsoft.license_management.usage_location.helptext',
        },
        licenses: {
          label: 'tkey;admin.microsoft.license_management.licenses.label',
          dataAutomation: 'ms-license-management-licenses',
          required: false,
          componentConfig: new ZiroReadWriteSelectFieldConfig({ availableOptions: sortBy(this.licensesOptions()) }),
          helpText: 'tkey;admin.microsoft.license_management.licenses.helptext',
        },
      },
    };
  }

  private _initBottomNav: () => void = () => {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'ms-license-management-save',
          dataAutomation: 'ms-license-management-save',
          label: 'tkey;global.button.save.text',
          icon: ZiroIcons.SAVE_SUCCESS,
          buttonClass: ButtonStyles.PRIMARY,
          cb: () => {
            this._validateAndSubmitSource.next(true);
          },
        },
      ]),
    );
  };

  protected submit(): Observable<void> {
    return this._onSubmit();
  }

  private _onSubmit(): Observable<void> {
    this._setPending('ms-license-management-save', true);
    const entityData = this._toApiData(this.entity);

    if (entityData.readOnlyLicenseList.length === 0 && entityData.writeLicenseList.length === 0) {
      const options = {
        windowClass: 'delete-button-modal',
        modalViewProperties: {
          title: 'tkey;admin.microsoft.license_management.licenses.modal.confirmation.title',
          promptBody: 'tkey;admin.microsoft.license_management.licenses.modal.confirmation.description',
          icon: ZiroIcons.WARNING_TRIANGLE,
          iconClass: 'text-warning',
          displayCloseButton: true,
          buttons: [
            {
              label: 'tkey;dialogs.button.cancel',
              buttonClass: ButtonStyles.OUTLINE_DEFAULT,
              dataAutomation: 'confirmation-modal-cancel-button',
            },
            {
              label: 'tkey;dialogs.button.ok',
              buttonClass: ButtonStyles.OUTLINE_PRIMARY,
              dataAutomation: 'confirmation-modal-confirm-button',
              cb: () => of(true),
            },
          ],
        },
      };
      return new Observable((subscriber) => {
        this._ziroModalService
          .openPromptModal(() => options.modalViewProperties, options)
          .subscribe((result) => {
            if (result) {
              this._putSettings().subscribe(() => {
                subscriber.next(null);
                subscriber.complete();
              });
            } else {
              this._setPending('ms-license-management-save', false);
              subscriber.next(null);
              subscriber.complete();
            }
          });
      });
    } else {
      return this._putSettings();
    }
  }

  private _putSettings(): Observable<any> {
    const entityData = this._toApiData(this.entity);

    return this._microsoft365LicenseSettingsResource.put(this.tenantId(), entityData).pipe(
      tap(() => {
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.MICROSOFT_365_SMALL,
          'tkey;admin.microsoft.license_management.title',
          'tkey;shared.toast.save.success.title',
        );
      }),
      catchError((error) => {
        return throwError(() => error);
      }),
      finalize(() => this._setPending('ms-license-management-save', false)),
    );
  }

  toFormData = (entity: MsLicenseManagementEntity): MsLicenseManagementFormData => {
    return {
      usage_location: {
        name: 'usage_location' as FrontEndFieldName,
        defaultOption: entity.usageLocation.defaultValue,
        possibleOptions: entity.usageLocation.possibleOptions,
        required: entity.usageLocation.required,
        show: entity.usageLocation.show,
      },
      licenses: {
        name: 'licenses' as FrontEndFieldName,
        defaultValues: [...entity.licenses.defaultValues],
        possibleOptions: entity.licenses.possibleOptions,
        required: entity.licenses.required,
        show: entity.licenses.show,
      },
    } as MsLicenseManagementFormData;
  };

  toEntity = (form: MsLicenseManagementFormData): MsLicenseManagementEntity => {
    return {
      usageLocation: {
        show: form.usage_location.show,
        required: form.usage_location.required,
        defaultValue: form.usage_location.defaultOption,
        possibleOptions: form.usage_location.possibleOptions,
      },
      licenses: {
        show: form.licenses.show,
        required: form.licenses.required,
        defaultValues: form.licenses.defaultValues,
        possibleOptions: form.licenses.possibleOptions,
      },
    };
  };

  private _toApiData(entity: MsLicenseManagementEntity): Microsoft365LicenseSettings {
    const readOnlyValues = difference(entity.licenses.possibleOptions, entity.licenses.defaultValues);
    const writeValues = entity.licenses.defaultValues;

    const readOnlyLicenses = readOnlyValues.map(
      (value: string) => this._findLicenseByProductName(value) || this._findLicenseBySkuPartNumber(value),
    );
    const writeLicenses = writeValues.map(
      (value: string) => this._findLicenseByProductName(value) || this._findLicenseBySkuPartNumber(value),
    );

    return {
      usageLocation: {
        defaultValue: entity.usageLocation.defaultValue,
        possibleOptions: entity.usageLocation.possibleOptions,
        required: entity.usageLocation.required,
        show: entity.usageLocation.show,
      },
      readOnlyLicenseList: readOnlyLicenses,
      writeLicenseList: writeLicenses,
    };
  }

  private _findLicenseByProductName(name: string): Microsoft365License {
    return this.ms365Licenses().find(
      (license: Microsoft365LicenseCount) => license.microsoft365LicenseJson.productName === name,
    )?.microsoft365LicenseJson;
  }

  private _findLicenseBySkuPartNumber(partNumber: string): Microsoft365License {
    return this.ms365Licenses().find(
      (license: Microsoft365LicenseCount) => license.microsoft365LicenseJson.skuPartNumber === partNumber,
    )?.microsoft365LicenseJson;
  }

  private _setPending(buttonId: string, setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: buttonId,
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }
}
